/**
 * InfinitSales - conversa com o painel parceiro no CRM embutido (2026-10-22).
 *
 * Montado no App, não renderiza nada e só age em modo embutido:
 * - avisa o painel a cada troca de rota (o menu dele acende o item certo);
 * - pede sessão ao carregar (o painel responde com o token em uso);
 * - recebe a renovação do token sem recarregar e, se a sessão tinha caído
 *   ou a conta mudou, reabre a sessão inteira;
 * - obedece à navegação pedida pelo painel, só entre as telas permitidas.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/global/authService';
import {
  EMBED_MSG, getEmbedState, isEmbedded, postToParent, routeForScreen,
  sessionFromMessage, setEmbedState,
} from '../utils/embed';
import { establishEmbedSession } from '../services/global/embedSession';

const EmbedListener = () => {
  const embedded = isEmbedded();
  const location = useLocation();
  const navigate = useNavigate();
  const { replaceToken } = useAuth();

  // O ouvinte nasce uma vez por carga; o que muda a cada render vai por ref,
  // para não repetir o pedido de sessão a cada troca de rota.
  const atual = useRef({});
  atual.current = { navigate, replaceToken };

  useEffect(() => {
    if (embedded) postToParent({ type: EMBED_MSG.ROUTE, path: location.pathname });
  }, [embedded, location.pathname]);

  useEffect(() => {
    // Na porta de entrada (/embed) quem conversa com o painel é a EmbedBridge;
    // duas vozes ali pediriam a sessão duas vezes.
    if (!embedded || window.location.pathname === '/embed') return undefined;
    const onMessage = (event) => {
      const st = getEmbedState();
      if (!st || event.origin !== st.origin) return;
      if (event.source && event.source !== window.parent) return;
      const m = event.data || {};
      if (m.type === EMBED_MSG.NAVIGATE) {
        const rota = routeForScreen(m.screen);
        if (rota) atual.current.navigate(rota, m.openChat ? { state: { openChat: m.openChat } } : undefined);
        return;
      }
      const sessao = sessionFromMessage(event);
      if (!sessao) return;
      // Decide pelo storage, não pelo estado do AuthContext: durante a carga
      // ele ainda não autenticou, e reabrir a sessão ali recarregaria em laço.
      const guardado = authService.getStoredUser();
      if (!guardado || !authService.getToken() || guardado.id !== sessao.payload.user_id) {
        establishEmbedSession({ ...sessao, screen: null }, window.location.pathname).catch(() => {});
        return;
      }
      atual.current.replaceToken(sessao.token);
      if (st.reloadOnToken) {
        // A sessão tinha caído no meio de uma chamada: recarrega para refazer
        // o que falhou, já com o token novo.
        setEmbedState({ ...st, reloadOnToken: false });
        window.location.reload();
      }
    };
    window.addEventListener('message', onMessage);
    postToParent({ type: EMBED_MSG.READY, resumed: true });
    return () => window.removeEventListener('message', onMessage);
  }, [embedded]);

  return null;
};

export default EmbedListener;
